import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { CreatableInput } from '@/components/forms/CreatableInput'
import { toast } from '@/components/toasts/toast.helpers'
import {
  createEmailIngestionBlocklistEntry,
  deleteEmailIngestionBlocklistEntry,
  fetchEmailIngestionBlocklist,
} from '@/reducers/organizations/emailIngestionBlocklist.actions'

const ENTRY_TYPE = {
  DOMAIN: 'domain',
  ADDRESS: 'address',
  PATTERN: 'pattern',
}

const toOption = (value) => ({ label: value, value })

// Trim + lowercase, plus the "leading-@" domain shorthand: a value that
// starts with '@' AND contains no other '@' (e.g. '@aol.com') is the
// "any address at this domain" shortcut, normalized to the bare domain so
// it round-trips with what the server stores. We deliberately leave the
// '@' alone for inputs like 'joe@aol.com' or 'joe@*.com' where the '@'
// carries semantics.
const normalize = (raw) => {
  const value = (raw || '').trim().toLowerCase()
  if (value.startsWith('@') && (value.match(/@/g) || []).length === 1) {
    return value.slice(1)
  }
  return value
}

// Detect the server entry_type from the raw value so the user never has to
// pick. Mirrors the server's validation rules (core-service _normalize_and_validate):
//   - has '*' or '?'     → pattern  (server also requires '@')
//   - has '@'            → address (server requires single '@', non-empty halves)
//   - else               → domain  (server strips a leading '@' and validates shape)
const detectEntryType = (value) => {
  if (/[*?]/.test(value)) {
    return ENTRY_TYPE.PATTERN
  }
  if (value.includes('@')) {
    return ENTRY_TYPE.ADDRESS
  }
  return ENTRY_TYPE.DOMAIN
}

// Permissive client check — reject only inputs that have no chance of
// matching ANY supported entry type. The server is authoritative on the rest;
// any 4xx surfaces as a toast on Save.
const looksLikeAnyEntry = (value) => {
  if (!value || /\s/.test(value)) {
    return false
  }
  // Must contain at least one structural character: '.', '@', or '*'.
  return /[.@*]/.test(value)
}

export const EmailDomainBlocklistButton = ({ organizationId }) => {
  const dispatch = useDispatch()
  const persistedEntries = useSelector((state) => state.emailIngestionBlocklist.entries)
  const [showModal, setShowModal] = useState(false)
  const [stagedOptions, setStagedOptions] = useState([]) // [{ label, value }]
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Load entries every time the modal opens so the staged list reflects
  // server state at open-time.
  useEffect(() => {
    if (!showModal || !organizationId) {
      return undefined
    }
    let cancelled = false
    setLoading(true)
    dispatch(fetchEmailIngestionBlocklist({ organizationId }))
      .then((entries) => {
        if (cancelled) {
          return
        }
        setStagedOptions(entries.map((entry) => toOption(entry.value)))
      })
      .catch(() => {
        if (cancelled) {
          return
        }
        setStagedOptions([])
        toast.error('Failed to load blocklist.')
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [showModal, organizationId, dispatch])

  const { toAdd, toDelete, dirty } = useMemo(() => {
    const stagedValues = stagedOptions.map((option) => option.value)
    const originalValues = persistedEntries.map((entry) => entry.value)
    const additions = stagedValues.filter((value) => value && !originalValues.includes(value))
    const removals = persistedEntries.filter((entry) => !stagedValues.includes(entry.value))
    return {
      toAdd: additions,
      toDelete: removals,
      dirty: !isEmpty(additions) || !isEmpty(removals),
    }
  }, [stagedOptions, persistedEntries])

  // Intercept CreatableInput's append-on-Enter behavior: normalize the new
  // pill (trim + lowercase), reject obviously invalid inputs with a toast,
  // and dedupe against existing pills. Type detection (domain / address /
  // pattern) happens at save time.
  const handleUpdateOptions = (_nameKey, nextOptions) => {
    const previousValues = new Set(stagedOptions.map((option) => option.value))
    const seen = new Set()
    const accepted = []
    const invalid = []
    nextOptions.forEach((option) => {
      const value = normalize(option.value)
      if (!value || seen.has(value)) {
        return
      }
      const isNewlyAdded = !previousValues.has(value)
      if (isNewlyAdded && !looksLikeAnyEntry(value)) {
        invalid.push(option.value)
        return
      }
      seen.add(value)
      accepted.push({ label: value, value })
    })
    if (!isEmpty(invalid)) {
      toast.error(
        `Not a recognizable entry: ${invalid.join(', ')}. Use a domain (example.com), address (user@example.com), or pattern (user@*.com).`
      )
    }
    setStagedOptions(accepted)
  }

  const handleCancel = () => {
    setShowModal(false)
  }

  const handleSave = async () => {
    if (!dirty || saving) {
      return
    }
    setSaving(true)
    const errors = []
    try {
      await Promise.all(
        toAdd.map(async (value) => {
          try {
            await dispatch(
              createEmailIngestionBlocklistEntry({
                organizationId,
                entryType: detectEntryType(value),
                value,
              })
            )
          } catch (err) {
            errors.push(`${value}: ${err?.error || err?.message || 'add failed'}`)
          }
        })
      )
      await Promise.all(
        toDelete.map(async (entry) => {
          try {
            await dispatch(
              deleteEmailIngestionBlocklistEntry({ organizationId, entryId: entry.id })
            )
          } catch (err) {
            errors.push(`remove ${entry.value}: ${err?.error || err?.message || 'delete failed'}`)
          }
        })
      )

      if (!isEmpty(errors)) {
        toast.error(`Some changes did not save: ${errors.join('; ')}`)
        // Refetch so the staged list reconciles with what's actually persisted
        // (partial successes shouldn't disappear without a trace).
        try {
          const entries = await dispatch(fetchEmailIngestionBlocklist({ organizationId }))
          setStagedOptions(entries.map((entry) => toOption(entry.value)))
        } catch (refetchErr) {
          // Already showed an error toast above; nothing more to do.
        }
      } else {
        setShowModal(false)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Button brand onClick={() => setShowModal(true)} data-testid="email-domain-blocklist-button">
        Email Domain Blocklist
      </Button>
      <BasicModal
        title="Email Domain Blocklist"
        show={showModal}
        onClose={handleCancel}
        canCloseOnDimmerClick
        footer={
          <div className="flex-align-center small-gap" style={{ justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel} data-testid="email-domain-blocklist-cancel">
              Cancel
            </Button>
            <Button
              brand
              onClick={handleSave}
              disabled={!dirty || saving || loading}
              loading={saving}
              data-testid="email-domain-blocklist-save"
            >
              Save
            </Button>
          </div>
        }
      >
        <p className="muted-text">
          Emails from blocked domains will be skipped during ingestion and won&apos;t appear in
          Explorer.
        </p>
        <CreatableInput
          value={stagedOptions}
          nameKey="domains"
          handleUpdateFilters={handleUpdateOptions}
          placeholder="example.com, user@example.com, or user@*.com"
          data-testid="email-domain-blocklist-input"
        />
      </BasicModal>
    </>
  )
}
